import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { axiosPrivate } from '../../modules/networkTools/networkTools';

type MedicalVisitSource = 'Manual' | 'Updated' | 'Data Feed';

export type MedicalVisitsItem = {
  id?: number;
  dateOfEncounter?: string | null;
  timeOfEncounter?: string | null;
  location?: string | null;
  type?: string | null;
  typeId?: number;
  provider?: string | null;
  reason?: string | null;
  status?: string | null;
  statusId?: number;
  source?: MedicalVisitSource;
};

export type MedicalVisitsFilters = {
  typeId?: string | string[] | null;
  statusId?: string | string[] | null;
  startDate?: string | null;
  endDate?: string | null;
};

type ClientId = { clientId: string | number; enableFileUpload?: boolean };

export const getMedicalVisitsPreview = createAsyncThunk(
  'medicalVisits/getMedicalVisitsPreview',
  async ({ clientId, enableFileUpload }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(
        `client/${clientId}/${enableFileUpload ? 'medical-visits' : 'clinical-encounters'}/preview`
      );
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisits = createAsyncThunk(
  'medicalVisits/getMedicalVisits',
  async (
    {
      clientId,
      page,
      size,
      sorts,
      filters,
      enableFileUpload
    }: {
      clientId: string | number;
      page: number;
      size: number;
      sorts: { title: string; direction: string }[] | null;
      filters: MedicalVisitsFilters | null;
      enableFileUpload?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      let params: string | string[] = '';
      if (Array.isArray(sorts) && sorts.length > 0) {
        params = sorts
          .filter((item) => item.direction && item.title)
          .map((item) =>
            item.title === 'dateTime'
              ? `&sort=dateOfEncounter,${item.direction}&sort=timeOfEncounter,${item.direction}`
              : `&sort=${item.title},${item.direction}`
          )
          .join('');
      }

      if (filters) {
        const filterParams = Object.entries(filters)
          .filter(([_, value]) => value != null && value !== '')
          .map(([key, value]) => `&${key}=${encodeURIComponent(String(value))}`)
          .join('');
        params += filterParams;
      }

      const response = await axiosPrivate.get(
        `client/${clientId}/${
          enableFileUpload ? 'medical-visits' : 'clinical-encounters'
        }?page=${page}&size=${size}${params}`
      );
      return { content: response.data, page, size, sorts, filters };
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const postMedicalVisits = createAsyncThunk(
  'medicalVisits/postMedicalVisits',
  async (
    {
      clientId,
      data,
      enableFileUpload
    }: { clientId: string | number; data: MedicalVisitsItem; enableFileUpload?: boolean },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.post(
        `client/${clientId}/${enableFileUpload ? 'medical-visits' : 'clinical-encounters'}`,
        data
      );
      return data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putMedicalVisits = createAsyncThunk(
  'medicalVisits/putMedicalVisits',
  async (
    {
      clientId,
      visitId,
      data,
      enableFileUpload
    }: {
      clientId: string | number;
      visitId: string | number;
      data: MedicalVisitsItem | any;
      enableFileUpload?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(
        `client/${clientId}/${
          enableFileUpload ? 'medical-visits' : 'clinical-encounters'
        }/${visitId}`,
        data
      );
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const deleteMedicalVisits = createAsyncThunk(
  'medicalVisits/deleteMedicalVisits',
  async (
    {
      clientId,
      visitId,
      enableFileUpload
    }: {
      clientId: string | number;
      visitId: string | number;
      enableFileUpload?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.delete(
        `client/${clientId}/${
          enableFileUpload ? 'medical-visits' : 'clinical-encounters'
        }/${visitId}`
      );

      return visitId;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisitsTypes = createAsyncThunk(
  'medicalVisits/getMedicalVisitsTypes',
  async ({ clientId, enableFileUpload }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(
        `client/${clientId}/${enableFileUpload ? 'medical-visits' : 'clinical-encounters'}/types`
      );
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisitsStatuses = createAsyncThunk(
  'medicalVisits/getMedicalVisitsStatuses',
  async ({ clientId, enableFileUpload }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(
        `client/${clientId}/${enableFileUpload ? 'medical-visits' : 'clinical-encounters'}/statuses`
      );
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
